<template>
  <div>
    <!-- DESCRIPTION -->
    <p>
      Clean up old chat sessions that have <code>genai_response_history</code> exceeding the
      configured history limit (default: 20 messages). This will trim the history to the limit for
      sessions last modified before the selected date. Process is batched to handle 1000 records at
      a time.
    </p>

    <!-- FORM -->
    <el-form label-width="120px">
      <!-- DRY RUN -->
      <el-form-item label="Dry Run">
        <el-tooltip
          content="When enabled, the cleanup will run in simulation mode without making actual changes to the database"
          placement="top"
        >
          <el-switch v-model="dryRun" />
        </el-tooltip>
      </el-form-item>

      <!-- DATE PICKER -->
      <el-form-item label="Before Date">
        <el-date-picker
          v-model="datePicked"
          type="date"
          placeholder="Select date"
          format="dd/MM/yyyy"
          :disabled="loading"
        />
        <el-tooltip
          content="Only sessions last modified BEFORE this date will be processed"
          placement="top"
        >
          <i class="el-icon-info" style="margin-left: 8px; color: #909399"></i>
        </el-tooltip>
      </el-form-item>

      <!-- BATCH SIZE -->
      <el-form-item label="Batch Size">
        <el-input-number
          v-model="batchSize"
          :min="100"
          :max="5000"
          :step="100"
          :disabled="loading"
        />
        <span style="margin-left: 8px; color: #909399; font-size: 12px"
          >Records per batch (100-5000)</span
        >
      </el-form-item>

      <!-- BUTTON -->
      <el-form-item>
        <el-button
          @click="onStart"
          type="primary"
          :loading="loading"
          :disabled="loading || !datePicked"
          >Start Cleanup</el-button
        >
      </el-form-item>
    </el-form>

    <!-- RESULT DIALOG -->
    <el-dialog title="Cleanup Results" :visible.sync="dialogVisible" width="800px">
      <div v-if="results">
        <el-alert
          v-if="results.dryRun"
          title="Dry Run Mode - No changes were made"
          type="warning"
          :closable="false"
          style="margin-bottom: 16px"
        />

        <!-- ALL AFFECTED RECORDS -->
        <div
          v-if="results.allAffectedRecords && results.allAffectedRecords.length > 0"
          style="margin-top: 20px"
        >
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px">
            <h4 style="margin: 0">All Affected Records ({{ results.allAffectedRecords.length }}):</h4>
            <el-button type="success" size="small" @click="exportToCSV">
              <i class="el-icon-download"></i> Export to CSV
            </el-button>
          </div>
          <el-table
            :data="paginatedRecords"
            size="small"
            style="margin-top: 10px"
            border
          >
            <el-table-column prop="PartitionKey" label="Partition Key" min-width="150" />
            <el-table-column prop="RowKey" label="Row Key" min-width="150" />
            <el-table-column prop="channel" label="Channel" width="100" />
            <el-table-column prop="lastModified" label="Last Modified" width="160" />
            <el-table-column prop="originalHistoryLength" label="Original Length" width="120" align="center" />
            <el-table-column prop="newHistoryLength" label="New Length" width="100" align="center" />
          </el-table>
          <el-pagination
            v-if="results.allAffectedRecords.length > pageSize"
            @current-change="handlePageChange"
            :current-page="currentPage"
            :page-size="pageSize"
            layout="total, prev, pager, next"
            :total="results.allAffectedRecords.length"
            style="margin-top: 16px; text-align: center"
          />
        </div>

        <!-- NO RECORDS AFFECTED -->
        <el-alert
          v-if="results.affectedRecords === 0"
          title="No records found that exceed the history limit"
          type="info"
          style="margin-top: 16px"
          :closable="false"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";

export default {
  data() {
    return {
      dryRun: true,
      datePicked: moment().subtract(30, "days").toDate(),
      batchSize: 100,
      results: null,
      loading: false,
      dialogVisible: false,
      currentPage: 1,
      pageSize: 20,
    };
  },
  computed: {
    paginatedRecords() {
      if (!this.results || !this.results.allAffectedRecords) {
        return [];
      }
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.results.allAffectedRecords.slice(start, end);
    },
  },
  methods: {
    handlePageChange(page) {
      this.currentPage = page;
    },
    exportToCSV() {
      if (!this.results || !this.results.allAffectedRecords || this.results.allAffectedRecords.length === 0) {
        this.$notify.warning({
          title: "Warning",
          message: "No records to export",
          position: "bottom-right",
        });
        return;
      }

      // Prepare CSV headers
      const headers = ["Partition Key", "Row Key", "Channel", "Last Modified", "Original Length", "New Length"];

      // Prepare CSV rows
      const rows = this.results.allAffectedRecords.map((record) => [
        record.PartitionKey,
        record.RowKey,
        record.channel || "N/A",
        record.lastModified,
        record.originalHistoryLength,
        record.newHistoryLength,
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", `genai_cleanup_results_${moment().format("YYYY-MM-DD_HHmmss")}.csv`);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.$notify.success({
        title: "Success",
        message: `Exported ${this.results.allAffectedRecords.length} records to CSV`,
        position: "bottom-right",
      });
    },
    async onStart() {
      try {
        const confirmMessage = this.dryRun
          ? "Run cleanup analysis? This will show how many records would be affected without making changes."
          : "Are you sure you want to cleanup chat history? This will permanently trim the history for affected sessions.";

        const confirmed = await this.$confirm(confirmMessage, "Warning", {
          confirmButtonText: "OK",
          cancelButtonText: "Cancel",
          type: this.dryRun ? "info" : "warning",
        });

        if (confirmed) {
          this.loading = true;

          const payload = {
            beforeDate: moment(this.datePicked).endOf("day").toISOString(),
            dryRun: this.dryRun,
            batchSize: this.batchSize,
          };

          const response = await this.$rest("post", "genai_cleanup_chat_history", payload);

          this.results = response;
          this.currentPage = 1; // Reset to first page
          this.dialogVisible = true;

          this.$notify.success({
            title: "Success",
            message: this.dryRun
              ? `Analysis complete: ${response.affectedRecords} records would be affected`
              : `Cleanup complete: ${response.affectedRecords} records updated`,
            position: "bottom-right",
          });
        }
      } catch (error) {
        console.error(error);
        this.$notify.error({
          title: "Error",
          message: _.get(error, "response.data.error") || _.toString(error),
          position: "bottom-right",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
}
</style>
